import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { GridService } from '../shared/grid/grid.service';
import { OrganizationLevel } from '../models/organizationLevel.model';
import { ToastService } from './toast.service';
import { Observable, Subject, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { toOrganizationTree } from '../_helpers/org-tree-helper';

@Injectable({
    providedIn: 'root'
})
export class OrganizationLevelService extends GridService<OrganizationLevel> {
    public organizationsUpdated = new Subject<boolean>();
    private static readonly tenantIdFilterKey = 'TenantId';

    private cache$: Observable<OrganizationLevel[]> | null = null;

    constructor(http: HttpClient, private toastService: ToastService) {
        super('OrganizationLevel', http);
    }

    override getAll(filterBy: string = ''): Observable<OrganizationLevel[]> {
        if (filterBy) {
            const params = new HttpParams().set('filterBy', filterBy);
            return this.http.get<OrganizationLevel[]>(this.getApiUrl(`${this.controllerName}/GetAll`), { params });
        }
        if (!this.cache$) {
            this.cache$ = this.http.get<OrganizationLevel[]>(this.getApiUrl(`${this.controllerName}/GetAll`)).pipe(
                shareReplay({ bufferSize: 1, refCount: true })
            );
        }
        return this.cache$;
    }

    // A super admin's GetAll spans every tenant (the API skips the tenant filter for them),
    // so a picker for another tenant's record is narrowed by the entity's TenantId.
    getTenantTree(tenantId: string): Observable<OrganizationLevel[]> {
        return this.getAll(JSON.stringify({ [OrganizationLevelService.tenantIdFilterKey]: tenantId })).pipe(
            map((levels) => toOrganizationTree(levels ?? [])),
            catchError((error: unknown) => {
                this.toastService.showError(error);
                return of<OrganizationLevel[]>([]);
            }),
        );
    }

    clearCache(): void {
        this.cache$ = null;
    }

    override create(payload: OrganizationLevel) {
        return super.create(payload).pipe(tap(() => this.clearCache()));
    }

    override update(payload: OrganizationLevel) {
        return super.update(payload).pipe(tap(() => this.clearCache()));
    }

    override delete(id: string) {
        return super.delete(id).pipe(tap(() => this.clearCache()));
    }

    override patch(id: string, payload: Partial<OrganizationLevel>) {
        return super.patch(id, payload).pipe(tap(() => this.clearCache()));
    }

    override createRange(payload: OrganizationLevel[]) {
        return super.createRange(payload).pipe(tap(() => this.clearCache()));
    }

    // null when the level has no Site above it (e.g. Corporate).
    getParentSite(organizationLevelId: string): Observable<Partial<OrganizationLevel> | null> {
        const params = new HttpParams().set('organizationLevelId', organizationLevelId);
        return this.http.get<Partial<OrganizationLevel> | null>(this.getApiUrl(`${this.controllerName}/GetParentSite`), { params });
    }
}
